import React, { useState } from 'react';
import { TEMPLATES, MATERIALS } from '../engine/rules';

export default function Rules() {
  const [tab, setTab] = useState<'templates'|'materials'|'rules'>('templates');
  return (
    <div className="fade-in">
      <div className="flex-between" style={{marginBottom:24}}>
        <div><h1>Engineering Rules</h1><p style={{marginTop:4,fontSize:13}}>Derived from 60+ production drawings · Sturdfix Ludhiana</p></div>
        <span className="badge badge-amber">v2.1 · March 2026</span>
      </div>
      <div className="tabs" style={{marginBottom:24}}>
        {[['templates','Templates'],['materials','Materials'],['rules','Key Rules']].map(([k,l])=>(
          <button key={k} className={`tab ${tab===k?'active':''}`} onClick={()=>setTab(k as any)}>{l}</button>
        ))}
      </div>

      {tab==='templates' && (
        <div className="grid-2">
          {Object.values(TEMPLATES).map((t:any)=>(
            <div key={t.name} className="card card-pad">
              <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:12}}>
                <div style={{width:48,height:48,background:'var(--c-navy)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{fontSize:18,fontWeight:800,color:'#fff'}}>{Object.keys(TEMPLATES).find(k=>(TEMPLATES as any)[k]===t)}</span>
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:'var(--t-primary)'}}>{t.name}</div>
                  <div style={{display:'flex',gap:6,marginTop:4}}>
                    <span className={`badge ${t.family==='BOLT'?'badge-amber':'badge-blue'}`}>{t.family}</span>
                    <span className="badge badge-gray">{t.stations} stations</span>
                  </div>
                </div>
              </div>
              <p style={{fontSize:12,color:'var(--t-muted)'}}>{t.trigger}</p>
            </div>
          ))}
        </div>
      )}

      {tab==='materials' && (
        <div className="card" style={{padding:0}}>
          <table>
            <thead><tr><th>Material</th><th>Grade</th><th>σ₀ (MPa)</th><th>K (MPa)</th><th>n</th><th>HT Quench</th><th>Target HRC</th><th>Application</th></tr></thead>
            <tbody>
              {Object.entries(MATERIALS).map(([id,m]:any)=>(
                <tr key={id}>
                  <td style={{fontWeight:600,fontFamily:'var(--mono)',fontSize:12}}>{id}</td>
                  <td><span className="badge badge-navy">{m.grade}</span></td>
                  <td style={{fontFamily:'var(--mono)',fontSize:12}}>{m.sigma0}</td>
                  <td style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--c-blue)',fontWeight:600}}>{m.K}</td>
                  <td style={{fontFamily:'var(--mono)',fontSize:12}}>{m.n}</td>
                  <td style={{fontSize:12}}>{m.ht_q?`${m.ht_q}°C`:'—'}</td>
                  <td style={{fontSize:12}}>{m.hrc||'—'}</td>
                  <td style={{fontSize:12,color:'var(--t-muted)'}}>{m.app}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab==='rules' && (
        <div className="grid-2">
          {KEY_RULES.map(s=>(
            <div key={s.title} className="card card-pad">
              <h3 style={{color:'var(--c-navy)',marginBottom:14,paddingBottom:10,borderBottom:'2px solid var(--c-border)'}}>{s.title}</h3>
              {s.rules.map((r,i)=>(
                <div key={i} style={{padding:'7px 0',borderBottom:i<s.rules.length-1?'1px solid var(--c-border)':'none'}}>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--t-primary)',marginBottom:2}}>{r.name}</div>
                  <div style={{fontSize:12,color:'var(--t-muted)'}}>{r.rule}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const KEY_RULES = [
  {title:'Template Selection',rules:[
    {name:'L/D Ratio',rule:'L/D > 4.0 → Template A (long bolt). L/D < 3.5 → Template B (short flange). 3.5–4.0 → borderline, engineer review.'},
    {name:'Set Screw',rule:'head_type = NONE + HEX_SOCKET → Template C, 2 stations, stripper mechanism mandatory.'},
    {name:'Hex formation station',rule:'D2 (plain hex): hex forms at Station 2. D1 (serrated flange): hex forms at Station 3 — one station later.'},
    {name:'Cone angle rule',rule:'Template B: always 9° regardless of wire dia. Template A: 6° if wire < 11mm, 9° if wire ≥ 11mm.'},
  ]},
  {title:'Machine Constants (Confirmed)',rules:[
    {name:'134L KO pin length',rule:'215mm — invariant for all parts on SJBF-134L and DBF-134L.'},
    {name:'164S KO pin length',rule:'215mm — same as 134L.'},
    {name:'Nut former KO lengths',rule:'11B6S=85mm, 14B6S=110mm, 19B6S=130mm, 24B6S=170mm. Monotonically increasing.'},
    {name:'KO pin body OD',rule:'Part-specific, NOT a machine constant. Body OD = die bore at that station − 0.10 to 0.15mm clearance.'},
  ]},
  {title:'Tooling Material Rules',rules:[
    {name:'Hex pin punch material',rule:'M42 if socket A/F ≤ 7mm. M2 if A/F > 7mm. Set screws always M42 regardless.'},
    {name:'TC pallet selection',rule:'Wire ≤10mm: Ø20×40. Wire 10–13mm front: Ø25×50, back: Ø25×40. Wire 13–16mm: Ø28×50 all stations.'},
    {name:'Pallet for grade marking',rule:'Carbide Ø35×35 when logo/grade impression needed. Steel Ø35×35 for plain finish.'},
    {name:'D2 front die',rule:'When external hex OD contacts die bore exit at Stn 3+, use D2 insert — NOT TC carbide.'},
  ]},
  {title:'Nut Former Rules',rules:[
    {name:'Die entry angle sequence',rule:'Stn 1: 35°. Stns 2–4: 30°. Stn 5: 5° taper only (no entry cone). Confirmed on M10 and M14.'},
    {name:'Springback allowance',rule:'Final die bore A/F = target A/F − 0.10 to −0.15mm for mild steel. Accounts for elastic recovery.'},
    {name:'Active KO stations',rule:'Universal rule: active KO at Stns 4 and 5 on all nut former templates. Stns 1–3 plain ejection only.'},
    {name:'Station 6 hole punch',rule:'Always M2, bore = minor thread dia − 0.15mm, no KO pin (slug free-fall), tool code batch 9999.'},
  ]},
];
